import {ActionTypes} from '../reducer/actions';
import {useEmailCommonHandlers} from './useEmailCommonHandlers';
import {LexicalEditor} from 'lexical';
import {getHtmlContent} from '../../../utils/lexical/getHtmlContent';
import {insertUrlContent} from '../utils/insertUrlContent';
import {insertIdTreeContent} from '../utils/insertIdTreeContent';
import {TUseEmailContextAPI} from './types';
import {TPersonalizeStates} from '../components/SideBar/parts/Personalize/hooks/types';
import {THyperlinkStates} from '../components/SideBar/parts/Hyperlink/hooks/types';
import {useContext} from 'react';
import {RuleDialogContext} from '../../Automations/components/RulesListDialog/context';
import {replaceTitlesWithIds, replaceTitlesWithIdsForHtml} from '../utils/treeActions';

export const useEmailContextAPI = ({dispatch, contextValue}: TUseEmailContextAPI) => {
    const {onBack} = useEmailCommonHandlers({dispatch});
    const {handleSaveNewEmailTemplate} = useContext(RuleDialogContext);

    const updatePersonalize = async (personalizeState: TPersonalizeStates) => {
        await insertIdTreeContent(
            contextValue.states.emailEditor,
            personalizeState.tree?.extraData.formTitle || '',
            personalizeState.tree?.title || ''
        );

        dispatch({type: ActionTypes.PERSONALIZE_UPDATE, payload: {personalizeState}});
    };

    const updateHyperlinkButton = async (hyperlinkStates: THyperlinkStates) => {
        await insertUrlContent(contextValue.states.emailEditor, hyperlinkStates);
        dispatch({
            type: ActionTypes.HYPERLINK_STATES_UPDATE,
            payload: {hyperlinkStates: {text: '', url: '', active: true}}
        });
    };

    const updateHyperlinkStates = async (hyperlinkStates: THyperlinkStates) => {
        dispatch({type: ActionTypes.HYPERLINK_STATES_UPDATE, payload: {hyperlinkStates}});
    };

    const updateActiveHyperlink = async (isActive: boolean) => {
        dispatch({type: ActionTypes.HYPERLINK_ACTIVE_UPDATE, payload: {isActive}});
        if (!isActive) {
            dispatch({
                type: ActionTypes.HYPERLINK_STATES_UPDATE,
                payload: {hyperlinkStates: {text: '', url: '', active: false}}
            });
        }
    };

    const updateImage = (imageState: string) => {
        dispatch({type: ActionTypes.IMAGE_UPDATE, payload: {imageState}});
    };

    const updateLanguage = (language: string) => {
        dispatch({type: ActionTypes.LANGUAGE_UPDATE, payload: {language}});
    };

    const updateEmailEditor = (editor: LexicalEditor) => {
        dispatch({type: ActionTypes.UPDATE_EMAIL_EDITOR, payload: {editor}});
    };

    const updateEmailHTML = async () => {
        const emailHtml = await getHtmlContent(
            contextValue.states.emailEditor,
            null,
            contextValue.states.language === '1'
        );

        dispatch({type: ActionTypes.UPDATE_EMAIL_HTML, payload: {emailHtml}});
        return {emailHtml, language: contextValue.states.language};
    };

    const updateEmailSubject = (subject: string) => {
        dispatch({type: ActionTypes.UPDATE_EMAIL_SUBJECT, payload: {subject}});
    };

    const saveEmailEditor = async ({htmlString, language, treeValues}, cb?: () => void) => {
        const subject = contextValue.states[language]?.editorSubject;
        handleSaveNewEmailTemplate({
            ...contextValue.states,
            [language]: {
                ...contextValue.states[language],
                editorSubjectIds: replaceTitlesWithIds(subject, treeValues),
                emailHtml: replaceTitlesWithIdsForHtml(htmlString, treeValues)
            }
        });
        cb?.();
        // dispatch({type: ActionTypes.SAVE_EMAILEDITOR});
    };

    return {
        onBack,
        updatePersonalize,
        updateHyperlinkButton,
        updateHyperlinkStates,
        updateActiveHyperlink,
        updateImage,
        updateLanguage,
        updateEmailEditor,
        updateEmailHTML,
        updateEmailSubject,
        saveEmailEditor
    } as const;
};
